import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { DrawSVGPlugin } from 'gsap/DrawSVGPlugin';
import { MorphSVGPlugin } from 'gsap/MorphSVGPlugin';
import { MotionPathPlugin } from 'gsap/MotionPathPlugin';

gsap.registerPlugin(ScrollTrigger, DrawSVGPlugin, MorphSVGPlugin, MotionPathPlugin);

export class ScrollTriggerSection {
    private container: HTMLElement;
    private messageDisplay: HTMLElement;
    private playheadDisplayBar: HTMLElement;
    private playheadDisplayText: HTMLElement;
    private section: HTMLElement;
    private currentPlayhead: number = 0;
    
    private messageElements: HTMLElement[] = [];

    private messages: string[] = [
        "Hi there!",
        "I'm an AI!",
        "Joke (😂)",
        "Not really",
        "I'm a web page",
        "made by meee, PK",
        "using Agentic AI",
        "I wondered",
        "how does it fare",
        "with more complex",
        "functionality?",
        "I started with",
        "Claude Code.",
        "It nailed matter.js",
        "and exploding heads.",
        "GSAP + ScrollTrigger?",
        "Struggled badly",
        "OMG so annoying",
        "I switched to", 
        "Cursor and Cline",
        "and used Gemini",
        "It was better",
        "but just wanted",
        "to reinvent the",
        "wheel",
        "And badly!",
        "It can see me",
        "typing this",
        "you worthless",
        "*ahem*",
        "Once pistol whipped",
        "into RTFM!",
        "it did just fine",
        "wolfie, just fine"
    ];

    constructor() {
        this.section = document.querySelector('#presentation .slide:first-child') as HTMLElement;
        if (!this.section) {
            console.error('ScrollTriggerSection: Could not find target section');
            return;
        }

        const existingContent = this.section.querySelector('.container');
        if (existingContent) {
            (existingContent as HTMLElement).style.display = 'none';
        }

        this.section.style.minHeight = '100vh';

        this.container = this.createContainer();
        this.messageDisplay = this.createMessageDisplayAndElements();
        this.createPlayheadElements();
        this.init();
    }

    private createContainer(): HTMLElement {
        const container = document.createElement('div');
        container.className = 'scroll-trigger-container';
        container.style.cssText = `
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            pointer-events: none;
            z-index: 10;
        `;
        this.section.style.position = 'relative';
        this.section.appendChild(container);
        return container;
    }

    private createMessageDisplayAndElements(): HTMLElement {
        const displayContainer = document.createElement('div');
        displayContainer.className = 'scroll-trigger-message-container';
        displayContainer.style.cssText = `
            position: relative;
            width: 90%;
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 10;
        `;

        this.messageElements = [];

        this.messages.forEach((text, index) => {
            const messageEl = document.createElement('div');
            messageEl.className = 'scroll-trigger-message-item';
            messageEl.textContent = text;
            messageEl.style.cssText = `
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 4rem;
                font-weight: 300;
                color: #333;
                text-align: center;
                white-space: normal;
                word-break: break-word;
                padding: 0 1rem;
            `;
            if (index === 0) {
                gsap.set(messageEl, { opacity: 1, y: 0 });
            } else {
                gsap.set(messageEl, { opacity: 0, y: 30 });
            }
            displayContainer.appendChild(messageEl);
            this.messageElements.push(messageEl);
        });
        
        this.container.appendChild(displayContainer);
        return displayContainer;
    }

    private createPlayheadElements(): void {
        this.playheadDisplayBar = document.createElement('div');
        this.playheadDisplayBar.className = 'scroll-trigger-playhead-bar';
        this.playheadDisplayBar.style.cssText = `
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            background-color: #000;
            padding: 0.25rem 0;
            z-index: 11;
            overflow: hidden;
        `;
        this.container.appendChild(this.playheadDisplayBar);

        this.playheadDisplayText = document.createElement('div');
        this.playheadDisplayText.className = 'scroll-trigger-playhead-text-content';
        this.playheadDisplayText.textContent = 'Section 2 - GSAP and ScrollTrigger Experiment - 0%';
        this.playheadDisplayText.style.cssText = `
            color: #fff;
            font-size: 0.8rem;
            font-weight: 400;
            font-family: monospace;
            white-space: nowrap;
            text-align: center;
            width: 100%;
        `;
        this.playheadDisplayBar.appendChild(this.playheadDisplayText);
    }

    private init(): void {
        if (!this.section || !this.messageDisplay || this.messageElements.length === 0) {
            console.error("ScrollTriggerSection: Essential elements not initialized for init.");
            return;
        }

        const pageHeight = window.innerHeight;
        const sectionHeight = this.section.offsetHeight;

        const individualAnimationTimes = {
            playheadEnterDuration: 0.5,    
            messageContainerEnterDelay: 0.6,
            messageContainerEnterDuration: 0.3,
            pinnedPhaseMessagesCycleDurationFactor: 1,
            pinnedPhaseTotalDuration: 3.0,
            playheadExitDuration: 0.5,     
            messageContainerExitDelayFromPhaseStart: 0.0, 
            messageContainerExitDuration: 0.3       
        };

        const actualScrollDistances = {
            prePinToPinStart: pageHeight, 
            pinDuration: individualAnimationTimes.pinnedPhaseTotalDuration * pageHeight, 
            postPinScrollDuration: 0.15 * pageHeight 
        };

        const timelinePhaseDurations = {
            prePin: actualScrollDistances.prePinToPinStart / pageHeight,
            pinned: individualAnimationTimes.pinnedPhaseTotalDuration,
            postPin: actualScrollDistances.postPinScrollDuration / pageHeight 
        };

        const masterTimeline = gsap.timeline();

        gsap.set(this.playheadDisplayText, { x: '100%', autoAlpha: 1 });
        gsap.set(this.messageDisplay, { x: '100vw', opacity: 1 });

        masterTimeline.to(this.playheadDisplayText, {
            x: '0%',
            duration: individualAnimationTimes.playheadEnterDuration,
            ease: 'none'
        }, 0); 

        masterTimeline.to(this.messageDisplay, {
            x: '0vw',
            duration: individualAnimationTimes.messageContainerEnterDuration,
            ease: 'power2.inOut'
        }, individualAnimationTimes.messageContainerEnterDelay); 

        const pinnedPhaseStartTimeOnGsap = timelinePhaseDurations.prePin;
        
        const messageSequenceTimeline = gsap.timeline();
        const numMessages = this.messageElements.length;
        const messagesCyclingGsapDuration = timelinePhaseDurations.pinned * individualAnimationTimes.pinnedPhaseMessagesCycleDurationFactor;

        if (numMessages > 0) {
            const transitionDuration = 0.3;
            const overlap = "-=" + (transitionDuration * 0.7);

            for (let i = 0; i < numMessages - 1; i++) {
                const currentMsgEl = this.messageElements[i];
                const nextMsgEl = this.messageElements[i+1];

                messageSequenceTimeline.to(currentMsgEl, {
                    opacity: 0,
                    y: -30,
                    duration: transitionDuration,
                    ease: "power1.in"
                });
                messageSequenceTimeline.to(nextMsgEl, {
                    opacity: 1,
                    y: 0,
                    duration: transitionDuration,
                    ease: "power1.out"
                }, overlap);
            }
            messageSequenceTimeline.duration(messagesCyclingGsapDuration);
        }

        masterTimeline.add(messageSequenceTimeline, pinnedPhaseStartTimeOnGsap);

        const remainingPinnedHoldDuration = timelinePhaseDurations.pinned - messagesCyclingGsapDuration;
        if (remainingPinnedHoldDuration > 0.01) {
            masterTimeline.to({}, { duration: remainingPinnedHoldDuration }, ">");
        }

        const postPinPhaseStartTimeOnGsap = pinnedPhaseStartTimeOnGsap + timelinePhaseDurations.pinned;

        masterTimeline.to(this.playheadDisplayText, {
            x: '-100%',
            duration: individualAnimationTimes.playheadExitDuration,
            ease: 'none'
        }, postPinPhaseStartTimeOnGsap);

        masterTimeline.to(this.messageDisplay, {
            x: '-100vw',
            duration: individualAnimationTimes.messageContainerExitDuration,
            ease: 'power2.inOut'
        }, postPinPhaseStartTimeOnGsap + individualAnimationTimes.messageContainerExitDelayFromPhaseStart);
        
        const totalActualScrollDistanceForAnimation = actualScrollDistances.prePinToPinStart + 
                                                  actualScrollDistances.pinDuration + 
                                                  actualScrollDistances.postPinScrollDuration;

        ScrollTrigger.create({
            trigger: this.section,
            animation: masterTimeline,
            start: 'top bottom', 
            end: () => `+=${totalActualScrollDistanceForAnimation}`,
            scrub: 1,
            markers: { startColor: "blue", endColor: "blue", fontSize: "10px", indent: 80 },
            onUpdate: (self) => {
                this.updatePlayhead(self.progress); 
            }
        });

        ScrollTrigger.create({
            trigger: this.section,
            start: 'top top', 
            end: () => `+=${actualScrollDistances.pinDuration}`, 
            pin: true, 
            pinSpacing: true, 
            markers: { startColor: "green", endColor: "red", fontSize: "10px" } 
        });
    }

    private updatePlayhead(progress: number): void {
        const percentage = Math.round(progress * 100);
        if (percentage !== this.currentPlayhead) {
            this.currentPlayhead = percentage;
            if (this.playheadDisplayText) {
                this.playheadDisplayText.textContent = `Section 2 - GSAP and ScrollTrigger Experiment - ${percentage}%`;
            }
        }
    }
}